import { CreditCard, Banknote, Smartphone, FileText, Store } from 'lucide-react';

/**
 * Shared payment vocabulary. Anything that takes money (academy programs today,
 * tournament registration next) should read methods and statuses from here
 * rather than restating them, so the rules stay in one place.
 *
 * These mirror the backend: see paymentStatusFor() in academiesController.js.
 */

export const PAYMENT_METHODS = [
  {
    id: 'card',
    label: 'Credit / debit card',
    Icon: CreditCard,
    // Settled by the payment provider — no human ever marks this one paid.
    manualConfirmation: false,
  },
  {
    id: 'cash',
    label: 'Cash',
    Icon: Banknote,
    manualConfirmation: true,
  },
  {
    id: 'venmo',
    label: 'Venmo',
    Icon: Smartphone,
    manualConfirmation: true,
  },
  {
    id: 'check',
    label: 'Check',
    Icon: FileText,
    manualConfirmation: true,
  },
  {
    id: 'other',
    label: 'Something else',
    Icon: Store,
    manualConfirmation: true,
  },
];

export const getPaymentMethod = (id) => PAYMENT_METHODS.find(m => m.id === id) || null;

export const paymentMethodLabel = (id) => getPaymentMethod(id)?.label || id || '—';

/**
 * True when a person has to verify the money actually arrived. Cash and Venmo
 * are only ever a claim by the payer; card is settled by the provider.
 */
export const requiresManualConfirmation = (id) => !!getPaymentMethod(id)?.manualConfirmation;

/** Per-method helper text. The confirmer differs by context (a coach for an
 *  academy program, the organizer for a tournament), so it's injected. */
export function paymentMethodNote(id, confirmerLabel = 'the organizer') {
  switch (id) {
    case 'card':  return 'Pay now by card — confirmed instantly';
    case 'cash':  return `Pay in person — ${confirmerLabel} will confirm receipt`;
    case 'venmo': return `Send via Venmo — ${confirmerLabel} will confirm receipt`;
    case 'check': return `Hand over a check — ${confirmerLabel} will record its number`;
    case 'other': return `Settle at the desk — ${confirmerLabel} will confirm receipt`;
    default:      return '';
  }
}

// ── Statuses ──────────────────────────────────────────────────────────────────

export const PAYMENT_STATUS = {
  unpaid:               { label: 'Unpaid',                tone: 'neutral' },
  pending_confirmation: { label: 'Awaiting confirmation', tone: 'warning' },
  paid:                 { label: 'Paid',                  tone: 'success' },
  refunded:             { label: 'Refunded',              tone: 'neutral' },
  failed:               { label: 'Failed',                tone: 'danger'  },
};

export const paymentStatusLabel = (status) => PAYMENT_STATUS[status]?.label || 'Unpaid';

/**
 * Whether a coach/organizer should be offered a "confirm payment" action.
 * Card is excluded even when unpaid — the provider owns that transition.
 */
export const canConfirmPayment = (enrollment = {}) =>
  enrollment.paymentStatus === 'pending_confirmation'
  && requiresManualConfirmation(enrollment.paymentMethod);

/**
 * Client-side mirror of the server's rule, for optimistic UI only — the server
 * remains the authority on what a registration's status actually becomes.
 */
export function expectedPaymentStatus(method, amount) {
  if (amount == null || Number(amount) === 0) return 'paid';
  return requiresManualConfirmation(method) ? 'pending_confirmation' : 'unpaid';
}

/**
 * Whether this registration still owes money that can be settled by card.
 *
 * Two ways to end up here. Abandoning Stripe — closing the tab, hitting back, a
 * declined card — leaves a real registration holding a real place with real
 * money owed. So does an entry that predates payment support, or one created
 * for a non-paying role and later upgraded to player: it owes a fee but no
 * method was ever chosen.
 *
 * Both need the same escape hatch, so a null method counts. Nobody can confirm
 * a card payment by hand — the provider owns that — and without a route back to
 * checkout the debt would simply sit there forever.
 */
export const needsCardPayment = (registration = {}) =>
  (registration.paymentMethod === 'card' || !registration.paymentMethod)
  && registration.paymentStatus === 'unpaid'
  && Number(registration.amountDue ?? 0) > 0;

/**
 * Only the person who made the registration can pay it — Stripe collects the
 * card, so an organizer clicking "pay" on someone else's entry would be
 * paying out of their own pocket. The server enforces this too.
 */
export const canPayFor = (registration = {}, userId) =>
  !!userId && (registration.createdBy === userId || registration.participantId === userId);

// ── Orders ────────────────────────────────────────────────────────────────────
//
// Everything above reads a registration's own payment columns, which nothing has
// written since the cutover. These read the ORDER instead, which is where the
// money now lives. The two coexist only so pre-cutover rows still render;
// anything new should use these.

/**
 * An order status expressed in the vocabulary the badge already speaks.
 *
 * `pending` is the interesting one: it means different things to a reader
 * depending on how they said they would pay. Nothing chosen yet reads "unpaid";
 * cash or a check reads "awaiting confirmation", because the payer has done
 * their part and a human at the desk has not done theirs.
 */
export const orderDisplayStatus = (order) => {
  if (!order) return 'unpaid';
  switch (order.status) {
    case 'paid':      return 'paid';
    case 'refunded':  return 'refunded';
    case 'failed':    return 'failed';
    case 'cancelled': return 'unpaid';
    default:
      return order.method && order.method !== 'card' && order.method !== 'stripe'
        ? 'pending_confirmation'
        : 'unpaid';
  }
};

/**
 * Whether this entry still owes money a card can settle.
 *
 * The order is the authority, so an entry paid by card no longer offers to
 * charge again — which is exactly what the legacy version did for every
 * post-cutover row, because it judged an unwritten column.
 */
export const orderNeedsCard = (order) =>
  !!order && order.status === 'pending' && Number(order.amount ?? 0) > 0;
